import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, MessageCircle, Sparkles, ScanLine, Car, FileSpreadsheet,
  ShieldCheck, Globe2, ReceiptText, CalendarClock,
} from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import { WHATSAPP_LINK } from '../utils/constants';

const STEPS = [
  { n: '1', title: 'Text it', desc: 'Message an income or expense in plain language — “$40 gas”, “got paid $1,200 by Acme”. No forms, no app to learn.' },
  { n: '2', title: 'AI categorizes', desc: 'MoneyFeed reads it, files it to the right tax line (Schedule C, VAT, income tax) and attaches any receipt you snap.' },
  { n: '3', title: 'Stays tracked', desc: 'Every entry rolls up into live income, expense and profit totals — plus mileage, bills and invoices.' },
  { n: '4', title: 'Tax-ready', desc: 'Export a CPA-ready Schedule C, P&L or VAT summary as PDF, CSV or Excel whenever you or your accountant needs it.' },
];

const FEATURES = [
  { icon: MessageCircle, title: 'WhatsApp-first bookkeeping', desc: 'Log income and expenses by texting — the tool lives where you already are.' },
  { icon: ScanLine, title: 'AI receipt & invoice scanning', desc: 'Snap a photo and MoneyFeed extracts the merchant, amount, date and tax category.' },
  { icon: Car, title: 'Mileage tracking', desc: 'Capture business trips and auto-apply the current mileage deduction rate.' },
  { icon: ReceiptText, title: 'Invoicing', desc: 'Create and send professional invoices, then track what has been paid.' },
  { icon: CalendarClock, title: 'Quarterly tax estimates', desc: 'See how much to set aside so quarterly (1040-ES) and year-end tax has no surprises.' },
  { icon: FileSpreadsheet, title: 'Accountant exports', desc: 'Hand your bookkeeper clean, categorized CSV/Excel with receipts attached.' },
];

const AUDIENCE = [
  'Freelancers & self-employed', '1099 & gig workers', 'Uber, Lyft & delivery drivers',
  'Etsy & online sellers', 'Consultants & contractors', 'Real estate agents',
  'Small business owners', 'Accountants & bookkeepers',
];

const COUNTRIES = [
  'United States (Schedule C / 1040-ES)', 'United Kingdom (Self Assessment)',
  'Canada (T2125)', 'Australia & New Zealand (sole trader)',
  'UAE & Saudi Arabia (VAT / ZATCA)', 'Europe (multi-currency income tax)',
];

const FAQS = [
  { q: 'What is MoneyFeed?', a: 'MoneyFeed is a WhatsApp tool that works with your bookkeeping app. Send receipts and expenses on WhatsApp and MoneyFeed returns clean, categorized records for QuickBooks, Xero, or Excel — no new software to learn.' },
  { q: 'How does MoneyFeed work?', a: 'You send a message on WhatsApp such as “$45 fuel” or “invoice paid $900”. MoneyFeed’s AI reads it, files it to the correct tax category, attaches any receipt photo, and updates your income, expense and profit totals. At tax time you export a CPA-ready summary.' },
  { q: 'Who is MoneyFeed for?', a: 'Freelancers, 1099 and gig workers, drivers, online sellers, consultants, contractors, small business owners, and the accountants who serve them — anyone who wants effortless, tax-ready bookkeeping without spreadsheets or accounting software.' },
  { q: 'Is MoneyFeed an accounting app?', a: 'No — MoneyFeed is a tool, not accounting software. It captures receipts, income and expenses on WhatsApp and produces clean, categorized exports (CSV, Excel, PDF) that import into QuickBooks or Xero, or go straight to your accountant.' },
  { q: 'Can MoneyFeed replace a spreadsheet?', a: 'Yes. Instead of manually typing rows into a spreadsheet, you text MoneyFeed and it keeps a categorized ledger for you. You can still export everything to Excel or CSV whenever you want the raw data.' },
  { q: 'Can MoneyFeed work with QuickBooks or my accountant?', a: 'MoneyFeed produces clean, categorized CSV and Excel exports plus receipt archives that you can import into QuickBooks, Xero or hand directly to your accountant, so it complements existing workflows rather than locking you in.' },
  { q: 'What taxes does MoneyFeed support?', a: 'US self-employment tax and IRS Schedule C / 1040-ES, UK Self Assessment, Canadian T2125, Australian and New Zealand sole-trader tax, and GCC VAT (UAE & Saudi Arabia / ZATCA), with multi-currency support elsewhere.' },
  { q: 'How does MoneyFeed handle receipts?', a: 'Snap a photo of any receipt or invoice on WhatsApp and MoneyFeed’s AI extracts the merchant, amount, date and tax category, then stores the image linked to the transaction as an audit trail.' },
  { q: 'Does MoneyFeed capture line items from receipts and invoices?', a: 'Yes. Every scan reads the invoice number and each line item with its amount, and the subtotals are checked against the total.' },
  { q: 'Can I fix a receipt after sending it on WhatsApp?', a: 'Yes. Reply ITEMS to see every line item of your last receipt, AMOUNT or CATEGORY to fix it, or UNDO to remove it. To change the invoice number or line items, tap the review link MoneyFeed sends with each receipt. MoneyFeed warns you if the totals do not reconcile.' },
  { q: 'What do I get in the weekly summary?', a: 'A WhatsApp summary with category totals for the week — just reply SUMMARY WEEK. Pro users can also download it as an Excel file.' },
  { q: 'Is MoneyFeed secure?', a: 'Your financial data is encrypted in transit and at rest, access is protected by phone-number and PIN authentication, and you can export or delete your data at any time. See our Privacy Policy and Data Protection page for details.' },
];

export default function AboutMoneyFeed() {
  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'Organization', name: 'MoneyFeed',
      alternateName: 'MoneyFeed AI', url: 'https://moneyfeed.io/',
      logo: 'https://moneyfeed.io/logo-mark.png',
      description: 'MoneyFeed is a WhatsApp tool that works with your bookkeeping app. Send receipts and expenses on WhatsApp and get clean, categorized records for QuickBooks, Xero, or Excel.',
      contactPoint: { '@type': 'ContactPoint', contactType: 'customer support', email: 'feed@moneyfeed.io', availableLanguage: ['English', 'Arabic', 'Spanish'] },
    },
    {
      '@context': 'https://schema.org', '@type': 'WebPage', name: 'What is MoneyFeed?',
      url: 'https://moneyfeed.io/about-moneyfeed',
      description: 'MoneyFeed is a WhatsApp tool that works with your bookkeeping app. Learn what it does, who it’s for, supported countries, taxes, receipts, invoicing and security.',
    },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="about-moneyfeed-page">
      <Seo
        title="What is MoneyFeed? Your Bookkeeping App, Now on WhatsApp"
        description="MoneyFeed is a WhatsApp tool that works with your bookkeeping app. Send receipts and expenses on WhatsApp and get clean, categorized records for QuickBooks, Xero, or Excel."
        path="/about-moneyfeed"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      {/* Hero / entity definition */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16]/15 rounded-full mb-5">
          <Sparkles className="h-4 w-4 text-[#65A30D]" />
          <span className="text-xs font-bold text-[#65A30D] uppercase tracking-wider">About MoneyFeed</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Your bookkeeping app, now on <span className="text-[#25D366]">WhatsApp</span>.
        </h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto mb-4">
          <strong>MoneyFeed is a WhatsApp tool that works with your bookkeeping app and your accountant.</strong> Text your income and expenses in plain language, snap receipts, and MoneyFeed keeps your books tax-ready — no spreadsheets and no accounting software to learn.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="about-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="about-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-3">How MoneyFeed works</h2>
        <p className="text-center text-gray-600 mb-10 max-w-xl mx-auto">Text → Categorize → Track → Tax-ready.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl shadow-md p-6" data-testid={`about-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white font-bold flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="font-bold text-[#064E3B] mb-1">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What you can do */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">What MoneyFeed does</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`about-feature-${f.title}`}>
              <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#065F46]" />
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{f.title}</h3>
              <p className="text-gray-600 text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for + Countries */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-md p-7" data-testid="about-audience">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4">Who MoneyFeed is for</h2>
            <div className="flex flex-wrap gap-2">
              {AUDIENCE.map((a) => (
                <span key={a} className="inline-block px-3 py-1.5 bg-[#ECFDF5] text-[#065F46] text-sm font-semibold rounded-full">{a}</span>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md p-7" data-testid="about-countries">
            <div className="flex items-center gap-2 mb-4">
              <Globe2 className="h-5 w-5 text-[#065F46]" />
              <h2 className="text-xl font-bold text-[#064E3B]">Countries & taxes supported</h2>
            </div>
            <ul className="space-y-2">
              {COUNTRIES.map((c) => (
                <li key={c} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-[#84CC16] font-bold mt-0.5">•</span> {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Security / company */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md p-7 flex items-start gap-4" data-testid="about-security">
          <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="h-5 w-5 text-[#065F46]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#064E3B] mb-1">Security & your data</h2>
            <p className="text-gray-600 text-sm">
              Your financial data is encrypted in transit and at rest, and access is protected by phone-number and PIN authentication. You own your data — export it any time or delete your account entirely. Read our{' '}
              <Link to="/privacy" className="text-[#065F46] font-semibold underline">Privacy Policy</Link>,{' '}
              <Link to="/terms" className="text-[#065F46] font-semibold underline">Terms</Link> and{' '}
              <Link to="/data-protection" className="text-[#065F46] font-semibold underline">Data Protection</Link> page. Questions? Email{' '}
              <a href="mailto:feed@moneyfeed.io" className="text-[#065F46] font-semibold underline">feed@moneyfeed.io</a>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">MoneyFeed, explained</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`about-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Do your books on WhatsApp</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free and text your first expense in the next 30 seconds — no spreadsheets, no learning curve.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="about-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-accounting" className="hover:text-[#064E3B]">WhatsApp Accounting</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
          <Link to="/demo" className="hover:text-[#064E3B]">See Demo</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
          <Link to="/features" className="hover:text-[#064E3B]" data-testid="about-link-features">MoneyFeed Features</Link>
          <Link to="/security" className="hover:text-[#064E3B]" data-testid="about-link-security">Security &amp; Data Protection</Link>
          <Link to="/supported-countries" className="hover:text-[#064E3B]" data-testid="about-link-supported-countries">Supported Countries</Link>
          <Link to="/tax-support" className="hover:text-[#064E3B]" data-testid="about-link-tax-support">Tax Support by Country</Link>
        </div>
      </footer>
    </div>
  );
}
